import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent,
  type ReactNode,
} from "react";
import { Link, Outlet, useNavigate } from "react-router";
import { useLiveEvents } from "@/api/live";
import { ScreenLoading } from "@/app/ScreenLoading";
import { HoldBanner } from "@/components/hold/HoldBanner";
import { useTheme } from "@/app/theme-context";
import { Glyph } from "@/components/ui/Glyph";
import { Pill } from "@/components/ui/controls";
import { CommandPalette } from "./CommandPalette";
import { Rail } from "./Rail";
import { ShellContext, type ShellValue } from "./shell-context";
import { GO_KEYS } from "./shortcuts";
import { ShortcutsOverlay } from "./ShortcutsOverlay";
import { StatusBar } from "./StatusBar";
import { TabBar } from "./TabBar";
import { useBusinessZone } from "./useBusinessZone";
import { useWorkspace } from "./useWorkspace";
import { Topbar, type PrimaryAction } from "./Topbar";
import { useGlobalShortcuts } from "./useGlobalShortcuts";
import { NARROW, PHONE, useMediaQuery } from "./useMediaQuery";
import styles from "./shell.module.css";

/** Moves focus to the screen's content, past the rail and the top bar. */
function SkipLink() {
  const skip = (e: MouseEvent<HTMLAnchorElement>) => {
    const main = document.getElementById("main");
    if (!main) return;
    e.preventDefault();
    main.focus();
    main.scrollTop = 0;
  };
  return (
    <a href="#main" className="zh-skip" onClick={skip}>
      Skip to content
    </a>
  );
}

/** While "G" waits for its letter, a quiet reminder of where each letter goes. */
function GoHint() {
  return (
    <div className={styles.goHint} role="status">
      <span className={styles.goTitle}>Go to</span>
      {GO_KEYS.map((g) => (
        <span key={g.key} className={styles.goItem}>
          <kbd className={styles.goKey}>{g.key.toUpperCase()}</kbd>
          {g.label}
        </span>
      ))}
    </div>
  );
}

/**
 * The app's frame: the rail (or, on a phone, the tab bar), the routed screen and the status
 * footer, plus the floating search and shortcuts that any screen can open.
 */
export function AppShell() {
  const ready = useWorkspace();
  return ready ? <Workspace /> : <ScreenLoading />;
}

/** The tenant's workspace, once its tenant is settled. */
function Workspace() {
  useLiveEvents();
  const zoned = useBusinessZone();
  const phone = useMediaQuery(PHONE);
  const navigate = useNavigate();
  const { toggle } = useTheme();
  const [palette, setPalette] = useState<string | null>(null);
  const [shortcuts, setShortcuts] = useState(false);

  const shell = useMemo<ShellValue>(
    () => ({
      available: true,
      openPalette: (query = "") => {
        setShortcuts(false);
        setPalette(query);
      },
      openShortcuts: () => {
        setPalette(null);
        setShortcuts(true);
      },
    }),
    [],
  );

  const going = useGlobalShortcuts({
    openPalette: shell.openPalette,
    openShortcuts: shell.openShortcuts,
    toggleTheme: toggle,
    go: (to) => void navigate(to),
  });

  return (
    <ShellContext.Provider value={shell}>
      <div className={styles.app}>
        <SkipLink />
        {phone ? null : <Rail />}
        <div className={styles.column}>
          <HoldBanner />
          {zoned ? <Outlet /> : <ScreenLoading />}
          {phone ? <TabBar /> : <StatusBar />}
        </div>
      </div>
      {palette !== null ? (
        <CommandPalette
          initialQuery={palette}
          onClose={() => setPalette(null)}
          onShortcuts={shell.openShortcuts}
        />
      ) : null}
      {shortcuts ? <ShortcutsOverlay onClose={() => setShortcuts(false)} /> : null}
      {going ? <GoHint /> : null}
    </ShellContext.Provider>
  );
}

/** The first words of a link, without the count beside them: "Open", not "Open12". */
function firstWords(el: Element): string {
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const parent = node.parentElement;
    if (parent?.closest(".visually-hidden, [aria-hidden='true']")) continue;
    const text = node.textContent?.trim();
    if (text) return text;
  }
  return "";
}

/**
 * On a narrow screen a screen's own menu (its views and filters) folds into a disclosure at the
 * top of the page, named for where you are in it. Choosing an item folds it away again.
 */
function SideMenu({
  heading,
  label: fallback,
  flush,
  children,
}: {
  heading: string;
  /** What to call the menu when nothing in it is current. */
  label: string;
  /** The screen's content runs to the edges, so the menu brings its own margin. */
  flush: boolean;
  children: ReactNode;
}) {
  const details = useRef<HTMLDetailsElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const label = useRef<HTMLSpanElement>(null);

  // The summary names the current items; it follows the menu as its links change.
  useLayoutEffect(() => {
    const box = panel.current;
    const out = label.current;
    if (!box || !out) return;
    const update = () => {
      const current = [...box.querySelectorAll('[aria-current="page"], [aria-current="true"]')].map(
        firstWords,
      );
      out.textContent = [...new Set(current.filter(Boolean))].join(" · ") || fallback;
    };
    update();
    const observer = new MutationObserver(update);
    observer.observe(box, {
      subtree: true,
      attributes: true,
      childList: true,
      characterData: true,
    });
    return () => observer.disconnect();
  }, [fallback]);

  const fold = (e: MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest("a") && details.current) details.current.open = false;
  };

  return (
    <details ref={details} className={`${styles.sideMenu} ${flush ? styles.sideMenuFlush : ""}`}>
      <summary className={styles.sideSummary}>
        <span className={styles.sideIcon} aria-hidden="true">
          <Glyph name="lines" size={15} strokeWidth={2.2} />
        </span>
        <span className="visually-hidden">{heading} menu, showing </span>
        <span ref={label} className={styles.sideLabel} />
        <Glyph name="chevronDown" size={15} strokeWidth={2.2} className={styles.sideChev} />
      </summary>
      <div ref={panel} className={styles.sidePanel} onClick={fold}>
        {children}
      </div>
    </details>
  );
}

interface PageProps {
  /** Shown in the header and the browser tab. */
  heading: string;
  title?: string;
  primary?: PrimaryAction | null;
  sidebar?: ReactNode;
  /** On a narrow screen the sidebar folds into a menu named for its current item, or this. */
  sidebarLabel?: string;
  children: ReactNode;
  /** Dotted canvas background, as on the overview screens. */
  dots?: boolean;
  mainStyle?: CSSProperties;
}

export function Page({
  heading,
  title,
  primary,
  sidebar,
  sidebarLabel,
  children,
  dots = true,
  mainStyle,
}: PageProps) {
  const narrow = useMediaQuery(NARROW);
  useEffect(() => {
    document.title = `${title ?? heading} · ZeaOps`;
  }, [heading, title]);
  return (
    <>
      <Topbar heading={heading} primary={primary} />
      <div className={styles.body}>
        {sidebar && !narrow ? (
          <aside className={styles.sidebar} aria-label={`${heading} menu`}>
            {sidebar}
          </aside>
        ) : null}
        <main
          id="main"
          tabIndex={-1}
          className={`${styles.main} ${dots ? "zh-dots" : ""}`}
          style={mainStyle}
        >
          {sidebar && narrow ? (
            <SideMenu
              heading={heading}
              label={sidebarLabel ?? `${heading} menu`}
              flush={mainStyle?.padding === 0}
            >
              {sidebar}
            </SideMenu>
          ) : null}
          {children}
        </main>
      </div>
    </>
  );
}

export function NavSection({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <nav className={styles.navSection} aria-label={title}>
      {title ? <div className={styles.navTitle}>{title}</div> : null}
      {children}
    </nav>
  );
}

export function NavItem({
  to,
  onClick,
  current,
  icon,
  swatch,
  count,
  countTone = "neutral",
  children,
}: {
  to?: string;
  onClick?: () => void;
  current?: boolean;
  icon?: ReactNode;
  swatch?: string;
  count?: number | string;
  countTone?: "neutral" | "hum" | "acc" | "crit" | "solid-hum";
  children: ReactNode;
}) {
  const inner = (
    <>
      {swatch ? <span className={styles.swatch} style={{ background: swatch }} /> : icon}
      <span>{children}</span>
      {count !== undefined ? (
        <span className={styles.navCount}>
          <Pill tone={countTone}>{count}</Pill>
        </span>
      ) : null}
    </>
  );
  if (to) {
    return (
      <Link to={to} className={styles.navLink} aria-current={current ? "page" : undefined}>
        {inner}
      </Link>
    );
  }
  return (
    <button
      type="button"
      className={`${styles.navLink} ${styles.navButton}`}
      aria-current={current ? "true" : undefined}
      onClick={onClick}
    >
      {inner}
    </button>
  );
}
